// Hideo - script editor, Fountain mode: what is drawn from a Fountain script (src/features/editor/fountain.ts), without
// UI: the screenplay pages of the preview and of the PDF (classes .fn-* in legacy.css), the teleprompter's text, the
// conversion to and from the visual page, and how the Fountain text is stored in a tab. Tests: tests/fountain.test.ts.
//
// Storage: a Fountain tab keeps its plain text in the tab's usual `html`, as one code block (<pre><code>…</code></pre>,
// escaped). It passes cleanHtml unchanged, counts and searches like any script, and an older version of the app (which
// does not know mode 'fountain' and opens the script in the visual mode) shows it as a code block, without losing it.
import { esc } from '../../utils/html'
import { validRef } from '../mentions'
import { noZw } from './html'
import {
  SCENE_RE, fountainTabHtml, inlineFountain, isCapsName, paginate, parseFountain, plainFountain,
  type FountainDoc, type FountainToken, type PagedToken, type TitleField
} from './fountain'

// ===== Storage =====

export { fountainTabHtml }

/** The Fountain text of a page (#scriptBody, or a template's HTML parsed): the code block written by fountainTabHtml as
 *  it is; any other page (a visual script, a tab changed by an older version) converted with htmlToFountain */
export function fountainFromPage(root: ParentNode): string {
  const kids = [...root.childNodes].filter(n => n.nodeType === 1 || (n.textContent || '').trim())
  if (kids.length === 1 && (kids[0] as Element).tagName === 'PRE') return (kids[0].textContent || '').replace(/\n$/, '')
  return htmlToFountain(root)
}
/** The Fountain text of some HTML */
export function fountainFromHtml(html: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = html || ''
  return fountainFromPage(tpl.content)
}

// ===== The screenplay pages =====

const CLS: Partial<Record<FountainToken['kind'], string>> = {
  scene: 'fn-scene', action: 'fn-action', character: 'fn-character', parenthetical: 'fn-paren', dialogue: 'fn-dialogue',
  lyrics: 'fn-lyrics', transition: 'fn-transition', centered: 'fn-centered', section: 'fn-section', synopsis: 'fn-synopsis', note: 'fn-note-block'
}
/** Title page fields: in the middle (title, credit, author, source), at the bottom left (draft date, contact…) and at the
 *  bottom right (notes, copyright) */
const TP_MAIN = ['title', 'credit', 'author', 'authors', 'source']
const TP_RIGHT = ['notes', 'copyright', 'revision']

function titlePageHtml(title: TitleField[]): string {
  const field = (f: TitleField) => `<div class="${f.key.toLowerCase() === 'title' ? 'fn-tp-title' : ''}">${f.values.map(v => inlineFountain(v, 'hide')).join('<br>')}</div>`
  const of = (keys: string[], keep: boolean) => title.filter(f => keys.includes(f.key.toLowerCase()) === keep)
  const main = TP_MAIN.flatMap(k => title.filter(f => f.key.toLowerCase() === k))
  const left = of([...TP_MAIN, ...TP_RIGHT], false), right = of(TP_RIGHT, true)
  return `<div class="fn-page fn-titlepage"><div class="fn-tp-main">${main.map(field).join('')}</div>` +
    `<div class="fn-tp-foot"><div>${left.map(field).join('')}</div><div class="fn-tp-right">${right.map(field).join('')}</div></div></div>`
}
function tokenHtml(p: PagedToken, screen: boolean): string {
  const tk = p.token, cls = CLS[tk.kind]
  if (!cls) return ''
  const sp = p.space ? ' fn-s1' : '', at = ` data-line="${tk.line}"`
  let body = inlineFountain(tk.text, screen ? 'show' : 'hide')
  if (tk.kind === 'scene' && tk.number) body = `<span class="fn-snum fn-snum-l">${esc(tk.number)}</span>${body}<span class="fn-snum fn-snum-r">${esc(tk.number)}</span>`
  if (tk.kind === 'section') body = '#'.repeat(tk.depth || 1) + ' ' + body
  if (tk.kind === 'note') body = `<span class="fn-note">${esc(tk.text)}</span>`
  return `<div class="fn-el ${cls}${sp}"${at}>${body}</div>`
}
/** One page's elements; a dual dialogue (its 'left' and 'right' runs) side by side */
function pageBody(page: PagedToken[], screen: boolean): string {
  let h = ''
  for (let i = 0; i < page.length; i++) {
    const tk = page[i].token
    if (!screen && !CLS[tk.kind]?.match(/^fn-(scene|action|character|paren|dialogue|lyrics|transition|centered)$/)) continue
    if (tk.dual === 'left') {
      const left: PagedToken[] = [], right: PagedToken[] = [], sp = page[i].space
      while (i < page.length && page[i].token.dual) { (page[i].token.dual === 'left' ? left : right).push({ token: page[i].token, space: 0 }); i++ }
      i--
      h += `<div class="fn-dual${sp ? ' fn-s1' : ''}"><div class="fn-dual-col">${left.map(x => tokenHtml(x, screen)).join('')}</div><div class="fn-dual-col">${right.map(x => tokenHtml(x, screen)).join('')}</div></div>`
      continue
    }
    h += tokenHtml(page[i], screen)
  }
  return h
}
/** The screenplay as pages (title page first, when the script has one; `fallbackTitle` makes one with that title when
 *  printing a script without it). Page numbers top right from page 2 ("2."), scene numbers in both margins when written.
 *  screen = the preview: notes, sections and synopses shown (they are never printed) */
export function screenplayHtml(doc: FountainDoc, opts: { screen: boolean; fallbackTitle?: string }): string {
  const { pages } = paginate(doc)
  const title = doc.title.length ? doc.title : opts.fallbackTitle ? [{ key: 'Title', values: [opts.fallbackTitle.replace(/[*_\\[\]]/g, '\\$&')] }] : []
  let h = title.length ? titlePageHtml(title) : ''
  pages.forEach((page, k) => { h += `<div class="fn-page">${k ? `<div class="fn-pnum">${k + 1}.</div>` : ''}${pageBody(page, opts.screen)}</div>` })
  return h
}

// ===== Teleprompter =====

/** What the teleprompter shows: scene headings as headings, cues in bold, parentheticals in italic, dialogue, action,
 *  transitions, centered text and lyrics; no title page, notes, boneyard, sections or synopses. Cleaned by the caller */
export function fountainPrompterHtml(src: string): string {
  return parseFountain(src).tokens.map(tk => {
    const x = inlineFountain(tk.text, 'hide')
    if (!plainFountain(tk.text).trim()) return ''
    switch (tk.kind) {
      case 'scene': return `<h2>${x}</h2>`
      case 'character': return `<p><strong>${x}</strong></p>`
      case 'parenthetical': case 'lyrics': return `<p><em>${x}</em></p>`
      case 'centered': return `<p style="text-align: center">${x}</p>`
      case 'action': case 'dialogue': case 'transition': return `<p>${x}</p>`
      default: return ''
    }
  }).join('')
}

// ===== Visual page ⇄ Fountain =====

/** Fountain → the visual page's HTML (cleaned by the caller): title page and action as paragraphs, a speech as one
 *  paragraph (cue in bold, then its lines), scene headings as Heading 2, sections as Heading 1–4, transitions on the
 *  right, centered text in the middle, synopses as quotes, page breaks as dividers; notes, lyrics and the boneyard keep
 *  their marks, so the text comes back the same */
export function fountainToHtml(src: string): string {
  const doc = parseFountain(src), x = (s: string) => inlineFountain(s, 'keep')
  let o = ''
  if (doc.title.length) o += '<p>' + doc.title.map(f => esc(f.key) + ': ' + (f.values.length === 1 ? x(f.values[0]) : f.values.map(v => '<br>&nbsp;&nbsp;&nbsp;' + x(v)).join(''))).join('<br>') + '</p>'
  const tk = doc.tokens
  for (let i = 0; i < tk.length; i++) {
    const t = tk[i]
    switch (t.kind) {
      case 'scene': o += `<h2>${x((t.forced && !SCENE_RE.test(t.text) ? '.' : '') + t.text + (t.number ? ` #${t.number}#` : ''))}</h2>`; break
      case 'section': o += `<h${Math.min(t.depth || 1, 4)}>${x(t.text)}</h${Math.min(t.depth || 1, 4)}>`; break
      case 'action': o += `<p>${x(t.text)}</p>`; break
      case 'character': {
        const lines = [`<strong>${x((t.forced && !isCapsName(t.text) ? '@' : '') + t.text + (t.dual === 'right' ? ' ^' : ''))}</strong>`]
        while (tk[i + 1] && (tk[i + 1].kind === 'parenthetical' || tk[i + 1].kind === 'dialogue')) lines.push(x(tk[++i].text))
        o += `<p>${lines.join('<br>')}</p>`
        break
      }
      case 'parenthetical': case 'dialogue': o += `<p>${x(t.text)}</p>`; break
      case 'transition': o += `<p style="text-align: right">${t.forced && !/TO:$/.test(t.text) ? '&gt; ' : ''}${x(t.text)}</p>`; break
      case 'centered': o += `<p style="text-align: center">${x(t.text)}</p>`; break
      case 'lyrics': o += `<p>${x('~' + t.text)}</p>`; break
      case 'synopsis': o += `<blockquote>${x(t.text)}</blockquote>`; break
      case 'note': o += `<p>${esc('[[' + t.text + ']]')}</p>`; break
      case 'boneyard': o += `<p>${esc('/*' + t.text + '*/').replace(/\n/g, '<br>')}</p>`; break
      case 'pageBreak': o += '<hr>'; break
    }
  }
  return o
}

/** The visual page → Fountain: headings that read as scene headings stay scene headings, the others become sections
 *  (# per level); paragraphs become action (a paragraph whose first line is a bold name in capitals becomes a speech),
 *  aligned right = transition, centered = >centered<; quotes = synopses; dividers = page breaks; list items and other
 *  blocks become lines of action. Bold, italic and underline become ** * _ (literal * and _ are escaped) */
export function htmlToFountain(root: ParentNode): string {
  const BL = /^(H[1-6]|P|DIV|UL|OL|LI|BLOCKQUOTE|HR|PRE|DETAILS|SUMMARY|TABLE|TR|SECTION|ARTICLE|HEADER|FOOTER|FIGURE)$/
  const inline = (n: Node): string => {
    if (n.nodeType === 3) return noZw(n.textContent).replace(/(?<!\/)\*(?!\/)|_/g, '\\$&').replace(/ /g, ' ')
    if (n.nodeType !== 1) return ''
    const el = n as HTMLElement, tag = el.tagName
    if (el.dataset && validRef(el.dataset.ref)) return noZw(el.textContent)   // a mention: its name
    if (tag === 'BR') return '\n'
    const c = [...el.childNodes].map(inline).join('')
    if (!c.trim()) return c
    if (tag === 'STRONG' || tag === 'B') return `**${c}**`
    if (tag === 'EM' || tag === 'I') return `*${c}*`
    if (tag === 'U') return `_${c}_`
    return c
  }
  const out: string[] = []
  const para = (s: string) => { const t = s.replace(/[ \t]+$/gm, '').replace(/^\n+|\n+$/g, ''); if (t.trim()) out.push(t) }
  const walk = (p: ParentNode) => {
    let buf = ''
    const flush = () => { para(buf); buf = '' }
    for (const n of [...p.childNodes]) {
      if (n.nodeType === 1 && BL.test((n as Element).tagName)) { flush(); block(n as HTMLElement) } else buf += inline(n)
    }
    flush()
  }
  const block = (el: HTMLElement) => {
    const tag = el.tagName, align = (el.style && el.style.textAlign) || ''
    if (/^H[1-6]$/.test(tag)) {
      const t = inline(el).replace(/\n/g, ' ').trim(), plainT = plainFountain(t)
      if (!t) return
      out.push(SCENE_RE.test(plainT) || /^\.(?!\.)/.test(t) ? plainT.toUpperCase() : '#'.repeat(Math.min(+tag[1], 6)) + ' ' + t)
      return
    }
    if (tag === 'HR') { out.push('==='); return }
    if (tag === 'PRE') { para(el.textContent || ''); return }
    if (tag === 'BLOCKQUOTE') { const t = inline(el).trim(); if (t) out.push(t.split('\n').map(l => '= ' + l.trim()).join('\n')); return }
    if (tag === 'UL' || tag === 'OL') { para([...el.children].map(li => '- ' + inline(li).trim().replace(/\n/g, ' ')).join('\n')); return }
    if (tag === 'P' || (tag === 'DIV' && ![...el.children].some(c => BL.test(c.tagName)))) {
      let t = inline(el).replace(/[ \t]+$/gm, '').replace(/^\n+|\n+$/g, '')
      if (!t.trim()) return
      if (align === 'right') { t = t.trim().toUpperCase(); out.push(/TO:$/.test(t) || t.startsWith('>') ? t : '> ' + t); return }
      if (align === 'center') { out.push('> ' + t.replace(/\n/g, ' ').trim() + ' <'); return }
      const lines = t.split('\n'), m = lines[0].match(/^\*\*(.+)\*\*$/)
      if (m && lines.length > 1 && isCapsName(m[1])) lines[0] = m[1].replace(/\\([*_])/g, '$1')
      para(lines.join('\n'))
      return
    }
    walk(el)
  }
  walk(root)
  return out.join('\n\n')
}
